import type { DataFile, LoadProgress } from '../data/types';
import { clamp } from '../core/math';

/* Bar shares from PLAN §5: data 20, validate 5, world stages 65, shader compileAsync 8, first
   frame 2. The first frame is drawn after Enter, when the bar is gone, so its two points go to the
   shader compile and the bar reaches 100% exactly when Enter becomes available. */
export const LOAD_SHARES = { download: 0.2, validate: 0.05, build: 0.65, compile: 0.1 } as const;

export const DATA_READY_FRACTION = LOAD_SHARES.download + LOAD_SHARES.validate;

export const COMPILE_START_FRACTION = DATA_READY_FRACTION + LOAD_SHARES.build;

export const PAINT_INTERVAL_MS = 1000 / 60;

/* Servers may omit Content-Length (compressed or chunked responses), so a file's size can be
   unknown until it has arrived. Every file therefore gets an equal share, credited in full when
   the next file starts; a file that does announce its size also advances within its share. */
export interface DownloadState {
  readonly fileCount: number;
  readonly finishedCount: number;
  readonly current: DataFile | null;
  readonly currentFraction: number;
}

export function createDownloadState(fileCount: number): DownloadState {
  return { fileCount, finishedCount: 0, current: null, currentFraction: 0 };
}

function withinFile(p: LoadProgress): number {
  return p.totalBytes !== null && p.totalBytes > 0 ? clamp(p.loadedBytes / p.totalBytes, 0, 1) : 0;
}

export function advanceDownload(state: DownloadState, p: LoadProgress): DownloadState {
  if (p.file === state.current) {
    return { ...state, currentFraction: Math.max(state.currentFraction, withinFile(p)) };
  }
  return {
    ...state,
    finishedCount: state.current === null ? state.finishedCount : state.finishedCount + 1,
    current: p.file,
    currentFraction: withinFile(p),
  };
}

export function downloadFraction(state: DownloadState): number {
  const files = Math.min(state.fileCount, state.finishedCount + state.currentFraction);
  return (LOAD_SHARES.download * files) / state.fileCount;
}

export function buildBarFraction(buildFraction: number): number {
  return DATA_READY_FRACTION + LOAD_SHARES.build * clamp(buildFraction, 0, 1);
}

/* A yield within one frame of the last paint would wait for the next vsync and show nothing new,
   which short stages (terrain features take a few ms) would pay in build time. Once a frame's
   worth of work has run, the next frame is already due and the paint is nearly free. */
export function isPaintDue(nowMs: number, lastPaintMs: number): boolean {
  return nowMs - lastPaintMs >= PAINT_INTERVAL_MS;
}
